import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Divider, IconButton, Paper, Skeleton, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import { colors } from '../../styles/theme';
import PanelShell from '../../Map/components/PanelShell/PanelShell';
import PanelHeader, { panelHeaderSubtitleSx } from '../../Map/components/PanelShell/PanelHeader';
import { DOCK_SCROLLBAR_SX } from '../../Map/components/DockPanel/Tabs/formBlockUi';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../hooks/useScrollEdgeFade';
import { useMarketComparison } from '../hooks/useMarketComparison';
import { useMarketComparablesLayer } from '../hooks/useMarketComparablesLayer';
import {
  MARKET_VERDICT_LABELS,
  formatMarketMonthly,
  formatMarketNumber,
  formatMarketRadius,
  pickMarketBlock,
} from '../marketComparison';
import { formatComparableChip, formatComparableTotal } from '../marketComparablesMap';
import MarketComparablesList from './MarketComparablesList';
import MarketComparisonDialog from './MarketComparisonDialog';
import {
  MARKET_FONT, MarketNotice, VerdictChip, marketLinkButtonSx, marketSkeletonSx, marketSmallSx,
} from './MarketParts';

const PANEL_BG = '#1c1c1c';
const PANEL_BORDER = '#4a4a4a';
const DEFAULT_RADIUS_M = 800;
const DEFAULT_MONTHS = 24;

/** Mismo botón «←» que la ficha del lote (`dockHeaderIconButtonSx` de DockPanel) y Listas. */
const backIconButtonSx = {
  color: '#E8F4F4',
  p: 0.5,
  mr: 0.25,
  '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.06)' },
};
const lineSx = { ...marketSmallSx, color: 'rgba(255,255,255,0.75)', lineHeight: '18px' };
const countSx = { ...marketSmallSx, lineHeight: '18px' };
const unitValueSx = {
  fontFamily: MARKET_FONT, fontSize: '14px', fontWeight: 700, color: colors.textPrimary, lineHeight: '18px',
};

/** Valor de la unidad (mismo criterio que el pin del mapa): alquiler con superficie = total por mes; si no, /m². */
export function marketUnitValueLabel(block) {
  const total = block?.monthly?.unitTotal;
  if (block?.operation === 'alquiler' && total !== null && total !== undefined) {
    return formatMarketMonthly(total, block.currency || 'ARS');
  }
  if (block?.unitValuePerM2 === null || block?.unitValuePerM2 === undefined) return '';
  return formatComparableChip(block.unitValuePerM2, { currency: block.currency, operation: block.operation });
}

/** Chip «Tu unidad» del mapa: alquiler con superficie en total por mes; si no, /m². */
export function marketUnitChipLabel(block) {
  const value = marketUnitValueLabel(block);
  return value ? `Tu unidad · ${value}` : 'Tu unidad';
}

/**
 * Superficie + total al lado del /m² (venta «60 m² · USD 120.000»;
 * alquiler solo «50 m²»: el total por mes ya va en `marketUnitValueLabel`).
 */
export function marketUnitSizeLine(block, unit) {
  const operation = block?.operation || unit?.operation || null;
  const currency = block?.currency || unit?.currency || null;
  const area = unit?.surfaceM2;
  const surface = area !== null && area !== undefined ? `${formatMarketNumber(area)} m²` : '';
  if (operation === 'alquiler') return surface;
  let total = null;
  if (block?.unitPriceTotal !== null && block?.unitPriceTotal !== undefined) total = block.unitPriceTotal;
  else if (unit?.price !== null && unit?.price !== undefined) {
    const sameCurrency = !unit?.currency || !currency || unit.currency === currency;
    if (sameCurrency) total = unit.price;
  } else if (area && block?.unitValuePerM2 !== null && block?.unitValuePerM2 !== undefined) {
    total = Math.round(block.unitValuePerM2 * area);
  }
  const totalLabel = formatComparableTotal({
    priceTotal: total,
    currency,
    operation,
    priceTotalPeriod: block?.priceTotalPeriod,
  });
  return [surface, totalLabel].filter(Boolean).join(' · ');
}

/** «Venta · 800 m · 24 meses · USD/m²» («… · ARS por mes» en alquiler) con los valores finales de la búsqueda. */
export function describeComparablesLine(block, view) {
  if (!block) return '';
  // Muestra baja (1 a 4, `low_sample`): sin mediana; los pines son solo de referencia.
  if (block.lowSample) return 'Pocos casos · solo de referencia';
  const radius = block.search?.radiusM ?? view?.radiusM ?? DEFAULT_RADIUS_M;
  const months = block.search?.months ?? view?.months ?? DEFAULT_MONTHS;
  const scope = block.search?.scope === 'radio_o_barrio' && block.search?.scopeLabel ? block.search.scopeLabel : '';
  return [
    block.operationLabel,
    scope || formatMarketRadius(radius),
    `${months} ${months === 1 ? 'mes' : 'meses'}`,
    // Alquiler: los chips son totales por mes (decisión 30 oct).
    block.operation === 'alquiler' ? `${block.currency || 'ARS'} por mes` : block.metricLabel,
  ].filter(Boolean).join(' · ');
}

/**
 * Modo «Comparables» del mapa principal (T3). Vista del Dock (en lugar de la ficha, que queda
 * montada y oculta) + capas en el mapa. Header estándar de los paneles del Dock (`PanelHeader`:
 * «←» con tooltip, título, «Encuadrar»; subtítulo = nombre + veredicto + valor de la unidad);
 * se sale con «←» (o cerrando el panel), que restaura pines y encuadre. La pide `MapPage` con el estado
 * `marketComparablesView` (lo arma la tarjeta / el modal con «Ver en el mapa»).
 * Ver docs/maps/market-comparison.md («Modo Comparables»).
 *
 * @param {{ mapInstanceRef: object, view: object, uiLeftInset?: number, fitPadding?: object|number,
 *   onExit: () => void, onChangeView: (patch: object) => void }} props
 */
export default function MarketComparablesMode({
  mapInstanceRef,
  view,
  uiLeftInset = undefined,
  fitPadding = 70,
  onExit,
  onChangeView,
}) {
  const filters = useMemo(() => ({
    radiusM: view.radiusM ?? undefined,
    months: view.months ?? undefined,
  }), [view.radiusM, view.months]);
  const { status, data, reload } = useMarketComparison({
    itemKind: view.itemKind,
    recordId: view.recordId,
    ownerUserId: view.ownerUserId ?? null,
    revision: view.revision ?? '',
    filters,
  });
  // Hover / selección atados a la lista vigente (por claves): con otra lista quedan en null
  // sin un efecto que los limpie a destiempo.
  const [hover, setHover] = useState({ sig: '', key: null });
  // Scroll solo en la lista: scrollbar oscura del Dock y fade arriba / abajo (ui-scroll-edge-fade).
  const { scrollRef, fadeTop, fadeBottom } = useScrollEdgeFade();
  const [selection, setSelection] = useState({ sig: '', key: null });
  const [reportOpen, setReportOpen] = useState(false);
  // El encuadre inicial usa `view.seq`. Cada clic de «Encuadrar» suma uno y el círculo vuelve al Dock.
  const [fitNonce, setFitNonce] = useState(0);

  useEffect(() => {
    if (status === 'hidden') onExit();
  }, [status, onExit]);

  const ready = status === 'ready' && data;
  const block = ready
    ? (data.blocks.find((b) => view.operation && b.operation === view.operation) || pickMarketBlock(data))
    : null;
  const comparables = useMemo(() => block?.comparables || [], [block]);
  const radiusM = block?.search?.radiusM ?? view.radiusM ?? DEFAULT_RADIUS_M;

  const keysSignature = comparables.map((c) => c.key).join('|');
  const hoveredKey = hover.sig === keysSignature ? hover.key : null;
  const selectedKey = selection.sig === keysSignature ? selection.key : null;
  const setHoveredKey = useCallback((key) => setHover({ sig: keysSignature, key: key ?? null }), [keysSignature]);
  const setSelectedKey = useCallback((key) => setSelection({ sig: keysSignature, key: key ?? null }), [keysSignature]);

  useMarketComparablesLayer({
    mapInstanceRef,
    enabled: true,
    center: view.center,
    radiusM,
    comparables,
    block,
    unitLabel: marketUnitChipLabel(block),
    hoveredKey,
    selectedKey,
    onHoverKey: setHoveredKey,
    onSelectKey: setSelectedKey,
    fitPadding,
    fitKey: fitNonce ? `${view.seq ?? ''}:${fitNonce}` : String(view.seq ?? ''),
  });

  const unitLabel = view.unitLabel || 'la unidad';
  const unitValue = marketUnitValueLabel(block);
  const unitSizeLine = marketUnitSizeLine(block, ready ? data.unit : null);
  const loadingHeader = !ready && status !== 'error';
  let body;
  if (!ready && status !== 'error') {
    body = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="market-mode-loading" aria-busy="true">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={84} sx={{ ...marketSkeletonSx, borderRadius: '10px' }} />)}
      </Box>
    );
  } else if (status === 'error' || !block) {
    body = (
      <MarketNotice
        testId="market-mode-error"
        title="No pudimos cargar los comparables"
        text="Probá de nuevo en un rato."
        actionLabel="Reintentar"
        onAction={reload}
      />
    );
  } else if (!comparables.length) {
    body = <MarketNotice testId="market-mode-empty" title="Sin comparables para mostrar" text="Probá con un radio o un período más amplio desde el informe." />;
  } else {
    body = (
      <MarketComparablesList
        block={block}
        hoveredKey={hoveredKey}
        selectedKey={selectedKey}
        onHoverKey={setHoveredKey}
        onSelectKey={setSelectedKey}
      />
    );
  }

  return (
    <>
      <PanelShell uiLeftInset={uiLeftInset} onClose={onExit}>
        <Paper
          elevation={0}
          data-testid="market-comparables-panel"
          sx={{
            flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%', minHeight: '100%',
            bgcolor: PANEL_BG, backgroundImage: 'none', color: colors.textPrimary,
            boxShadow: '0 18px 45px rgba(15,23,42,0.55)', border: `1px solid ${PANEL_BORDER}`,
            borderRadius: { xs: 0, sm: '16px' },
          }}
        >
          <Box sx={{ bgcolor: PANEL_BG, flexShrink: 0 }}>
            <PanelHeader
              title="COMPARABLES"
              subtitle={(
                // Alto fijo (nombre + valor · m² · total): el chip y el precio entran sin saltar el layout.
                <Box sx={{ height: 46, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4px' }} data-testid="market-mode-unit">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, height: 24 }}>
                    <Typography
                      sx={{ ...panelHeaderSubtitleSx, flex: 1, minWidth: 0 }}
                      noWrap
                      title={unitLabel}
                      data-testid="market-mode-unit-name"
                    >
                      {unitLabel}
                    </Typography>
                    {block?.verdict ? (
                      <VerdictChip
                        verdict={block.verdict}
                        label={MARKET_VERDICT_LABELS[block.verdict]}
                        size="sm"
                        testId="market-mode-chip"
                      />
                    ) : loadingHeader ? (
                      <Skeleton variant="rounded" width={72} height={20} sx={{ ...marketSkeletonSx, borderRadius: '999px', flexShrink: 0 }} />
                    ) : null}
                  </Box>
                  {unitValue || unitSizeLine ? (
                    <Typography sx={unitValueSx} noWrap>
                      {unitValue ? <span data-testid="market-mode-unit-value">{unitValue}</span> : null}
                      {unitValue && unitSizeLine ? ' · ' : null}
                      {unitSizeLine ? <span data-testid="market-mode-unit-size">{unitSizeLine}</span> : null}
                    </Typography>
                  ) : loadingHeader ? (
                    <Skeleton variant="rounded" width={220} height={14} sx={marketSkeletonSx} />
                  ) : (
                    <Typography sx={unitValueSx} noWrap data-testid="market-mode-unit-value">{'\u00A0'}</Typography>
                  )}
                </Box>
              )}
              startAdornment={(
                <Tooltip title={`Volver a ${unitLabel}`} placement="top" arrow>
                  <IconButton
                    onClick={onExit}
                    size="small"
                    aria-label={`Volver a ${unitLabel}`}
                    sx={backIconButtonSx}
                    data-testid="market-mode-back"
                  >
                    <ArrowBackIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              )}
              actions={(
                <Tooltip title="Encuadrar en el mapa" placement="bottom" arrow>
                  <IconButton
                    onClick={() => setFitNonce((n) => n + 1)}
                    size="small"
                    aria-label="Encuadrar en el mapa"
                    sx={{
                      color: '#E8F4F4',
                      '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.06)' },
                    }}
                    data-testid="market-mode-fit"
                  >
                    <FitScreenIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              description={(
                // Alto fijo (dos líneas): sin saltos entre carga, lista, vacío y error.
                <Box sx={{ height: 40, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4px' }}>
                  {ready ? (
                    <>
                      <Typography sx={lineSx} noWrap data-testid="market-mode-line">{describeComparablesLine(block, view)}</Typography>
                      <Typography sx={countSx} noWrap data-testid="market-mode-count">
                        {block && comparables.length
                          ? `${comparables.length}${block.nComparables > comparables.length ? ` de ${block.nComparables}` : ''} · más parecidas primero`
                          : '\u00A0'}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Skeleton variant="rounded" width={220} height={12} sx={marketSkeletonSx} />
                      <Skeleton variant="rounded" width={150} height={12} sx={marketSkeletonSx} />
                    </>
                  )}
                </Box>
              )}
            />
          </Box>
          <Box
            ref={scrollRef}
            sx={{
              flex: 1, minHeight: 0, overflowY: 'auto', px: 2, pt: 0.5, pb: 2,
              ...DOCK_SCROLLBAR_SX,
              scrollbarGutter: 'stable',
              ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
            }}
            data-testid="market-mode-body"
          >
            {body}
          </Box>
          <Divider sx={{ borderColor: '#333' }} />
          <Box sx={{ px: 2, py: 1, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }} data-testid="market-mode-footer">
            <Button size="small" onClick={() => setReportOpen(true)} disabled={!ready} sx={marketLinkButtonSx}>Ver informe ›</Button>
          </Box>
        </Paper>
      </PanelShell>

      {reportOpen ? (
        <MarketComparisonDialog
          itemKind={view.itemKind}
          recordId={view.recordId}
          ownerUserId={view.ownerUserId ?? null}
          revision={view.revision ?? ''}
          unitLabel={view.unitLabel || ''}
          initialRadiusM={view.radiusM ?? null}
          initialMonths={view.months ?? null}
          initialOperation={block?.operation || view.operation || null}
          onClose={() => setReportOpen(false)}
          onShowOnMap={(next) => onChangeView(next)}
        />
      ) : null}
    </>
  );
}
